import React from 'react'
import { cn } from '@/lib/utils'

export type TextVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body1' | 'body2' | 'caption'

type TextElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div'

export interface TextProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Typography variant that determines styling and default semantic element
   * @default 'body1'
   */
  variant?: TextVariant
  /**
   * Override the default semantic HTML element
   */
  as?: TextElement
  /**
   * Custom className for additional styling
   */
  className?: string
  /**
   * Child content
   */
  children?: React.ReactNode
}

/**
 * Text component for typography with semantic HTML mapping
 *
 * Replacement for MUI Typography with Tailwind CSS styling
 *
 * @example
 * ```tsx
 * <Text variant="h1">Page Title</Text>
 * <Text variant="body1">Regular paragraph text</Text>
 * <Text variant="caption">Small caption text</Text>
 * ```
 *
 * @example
 * ```tsx
 * // Render h1 styling as h2 element (for SEO/accessibility)
 * <Text variant="h1" as="h2">Section Title</Text>
 * ```
 */
export const Text = React.forwardRef<HTMLElement, TextProps>(
  ({ variant = 'body1', as, className, children, ...props }, ref) => {
    // Map variants to default semantic elements
    const defaultElements: Record<TextVariant, TextElement> = {
      h1: 'h1',
      h2: 'h2',
      h3: 'h3',
      h4: 'h4',
      h5: 'h5',
      h6: 'h6',
      body1: 'p',
      body2: 'p',
      caption: 'span'
    }

    // Map variants to Tailwind typography classes
    const variantClasses: Record<TextVariant, string> = {
      h1: 'text-4xl font-bold',
      h2: 'text-3xl font-semibold',
      h3: 'text-2xl font-semibold',
      h4: 'text-xl font-medium',
      h5: 'text-lg font-medium',
      h6: 'text-base font-medium',
      body1: 'text-base',
      body2: 'text-sm',
      caption: 'text-xs text-muted-foreground'
    }

    // Use provided element or default for variant
    const Element = (as || defaultElements[variant]) as React.ElementType

    return (
      <Element
        ref={ref}
        className={cn(variantClasses[variant], className)}
        {...props}
      >
        {children}
      </Element>
    )
  }
)

Text.displayName = 'Text'
